import React, { useState, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TimeLineTrendChart from '../Timeline/TimeLineTrendChart';
import CalendarOptimized from '../CalendarOptimized';
import { useWebSocket, useUser } from '../WebSocketContext';
import WidgetLoadingSkeleton from '../Widgets/WidgetLoadingSkeleton';
import WidgetDashboardHeader from './WidgetDashboardHeader';
import ProjectChat from '../ProjectChat/ProjectChat';
import useLocalStorageFlag from '../../hooks/useLocalStorageFlag.js';
import {
  DASHBOARD_PROJECT_VIEW,
  FLOWS_PROJECT_VIEW,
  readProjectViewForSession,
  saveProjectViewForSession,
} from '../../Utils/projectViewSession';
import { fetchProjectTemplateSetupStatus } from '../../Utils/actionsApi';
import useNavigationStorage from '../../hooks/useNavigationStorage';
import useProjectDagViewportHeight from '../ProjectDag/useProjectDagViewportHeight';

let widgetsModulePromise;
export const loadWidgetsModule = () => {
  if (!widgetsModulePromise) {
    widgetsModulePromise = import('../Widgets/Widgets').catch((error) => {
      widgetsModulePromise = null;
      throw error;
    });
  }
  return widgetsModulePromise;
};
const Widgets = React.lazy(loadWidgetsModule);
const AddWidgetModal = React.lazy(() => import('../Widgets/AddWidgetModal'));
const ProjectDagView = React.lazy(() => import('../ProjectDag/ProjectDagView'));
const DAG_VIEW_ID = FLOWS_PROJECT_VIEW;

const WidgetDashboard = ({
  caseid,
  folder = "",
  templateInfo = null,
  onOpenTimeline,
  showCalendarWidget = true,
  showTrendWidget = true,
  autoOpenWidgetTag = null,
  onAutoOpenWidgetTagHandled = null,
  onChatAvailabilityChange,
  projectViewContainer = null
}) => {
  const { t } = useTranslation();
  const user = useUser();
  const navigationStorage = useNavigationStorage();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const flowInteractionType = searchParams.get('flowInteraction');
  const openActionId = searchParams.get('openActions') === 'true'
    ? String(searchParams.get('exportId') || '').trim()
    : '';
  const flowInteractionToken = String(searchParams.get('flowInteractionToken') || '').trim();
  const actionTestEventId = String(searchParams.get('testEventId') || '').trim();
  const initialFlowInteraction = useMemo(() => (
    openActionId
      ? {
        type: 'action',
        actionId: openActionId,
        testEventId: actionTestEventId,
        token: flowInteractionToken || `dag-action-${caseid}-${openActionId}`,
      }
      : ['action', 'connector', 'flow'].includes(flowInteractionType)
      ? {
        type: flowInteractionType,
        token: `flow-interaction-${caseid}-${flowInteractionType}`,
      }
      : null
  ), [caseid, flowInteractionToken, flowInteractionType, openActionId, actionTestEventId]);

  const handleInitialFlowInteractionHandled = useCallback(() => {
    if (!flowInteractionType && !openActionId) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('flowInteraction');
    nextParams.delete('openActions');
    nextParams.delete('exportId');
    nextParams.delete('flowInteractionToken');
    nextParams.delete('testEventId');
    setSearchParams(nextParams, { replace: true });
  }, [flowInteractionType, openActionId, searchParams, setSearchParams]);

  // Add widget modal state
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [projectView, setProjectView] = useState(
    () => searchParams.get('openTemplateSetup') === 'true'
      ? FLOWS_PROJECT_VIEW : readProjectViewForSession(caseid, navigationStorage)
  );

  // Snapshot state
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState('');
  const [activeSnapshot, setActiveSnapshot] = useState(null);
  const [snapshotOverrides, setSnapshotOverrides] = useState({});
  const [snapshotError, setSnapshotError] = useState('');
  const [templateSetupStatusCheckFailed, setTemplateSetupStatusCheckFailed] = useState(false);
  const [isSavingSnapshot, setIsSavingSnapshot] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveComment, setSaveComment] = useState('');
  const [saveError, setSaveError] = useState('');
  const [showHiddenWidgets, setShowHiddenWidgets] = useLocalStorageFlag('widgetShowHiddenWidgets', false);
  const canManageProject = Boolean(user.canManageProject);
  const effectiveShowHiddenWidgets = canManageProject ? showHiddenWidgets : false;

  const isSnapshotMode = Boolean(activeSnapshot);
  const isDagMode = projectView === DAG_VIEW_ID;
  const flowViewportRef = useRef(null);
  useProjectDagViewportHeight(flowViewportRef, isDagMode);

  useEffect(() => {
    setProjectView(searchParams.get('openTemplateSetup') === 'true'
      ? saveProjectViewForSession(caseid, DAG_VIEW_ID, navigationStorage)
      : readProjectViewForSession(caseid, navigationStorage));
  }, [caseid, navigationStorage, searchParams]);

  useEffect(() => {
    if (openActionId && !isDagMode) {
      setProjectView(saveProjectViewForSession(caseid, DAG_VIEW_ID, navigationStorage));
    }
  }, [caseid, isDagMode, openActionId, navigationStorage]);

  const handleProjectViewSelect = useCallback((nextView) => {
    setProjectView(saveProjectViewForSession(caseid, nextView, navigationStorage));
  }, [caseid, navigationStorage]);
  const handleOpenDashboard = useCallback(() => {
    handleProjectViewSelect(DASHBOARD_PROJECT_VIEW);
  }, [handleProjectViewSelect]);

  useEffect(() => {
    setTemplateSetupStatusCheckFailed(false);
    if (!caseid || !canManageProject || isDagMode) return undefined;
    let isCurrent = true;
    fetchProjectTemplateSetupStatus(caseid)
      .then((status) => {
        if (
          isCurrent
          && status?.has_incomplete_steps === true
        ) {
          setProjectView(saveProjectViewForSession(caseid, DAG_VIEW_ID, navigationStorage));
        }
      })
      .catch(() => {
        if (isCurrent) setTemplateSetupStatusCheckFailed(true);
      });
    return () => { isCurrent = false; };
  }, [canManageProject, caseid, isDagMode, navigationStorage]);

  const toggleShowHiddenWidgets = useCallback(() => {
    setShowHiddenWidgets((prev) => !prev);
  }, [setShowHiddenWidgets]);

  const refreshSnapshots = useCallback(() => {
    if (!sendMessage || !caseid) {
      return;
    }
    setSnapshotsLoading(true);
    sendMessage({
      command: 'getDashboardSnapshots',
      caseid
    });
  }, [sendMessage, caseid]);

  const parseSnapshotPayload = useCallback((rawValue) => {
    if (!rawValue) return null;
    if (typeof rawValue === 'string') {
      try {
        return JSON.parse(rawValue);
      } catch (err) {
        return null;
      }
    }
    return rawValue;
  }, []);

  const normalizeSnapshotAnswer = useCallback((value) => {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object') {
          return normalizeSnapshotAnswer(parsed);
        }
      } catch (err) {
        return { answer: value };
      }
      return { answer: value };
    }
    if (typeof value === 'object') {
      if (value.answer !== undefined) {
        const normalizedAnswer = typeof value.answer === 'string'
          ? value.answer
          : JSON.stringify(value.answer, null, 2);
        return { ...value, answer: normalizedAnswer };
      }
      return { answer: JSON.stringify(value, null, 2) };
    }
    return { answer: String(value) };
  }, []);

  const buildSnapshotOverrides = useCallback((logs) => {
    const overrides = {};
    (logs || []).forEach((log) => {
      const snapshotData = parseSnapshotPayload(log?.widget_snapshot);
      const widgetId = snapshotData?.widget?.id;
      if (!widgetId) {
        return;
      }
      const response = snapshotData?.new_response ?? snapshotData?.previous_response;
      const normalizedAnswer = normalizeSnapshotAnswer(response);
      if (!normalizedAnswer) {
        return;
      }
      overrides[widgetId.toString()] = {
        answer: normalizedAnswer,
        logId: log?.id,
        snapshotTakenAt: snapshotData?.snapshot_taken_at,
        snapshotData
      };
    });
    return overrides;
  }, [normalizeSnapshotAnswer, parseSnapshotPayload]);

  const normalizeSnapshotListItem = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') {
      return null;
    }
    const historyIds = Array.isArray(snapshot.widget_history_ids)
      ? snapshot.widget_history_ids
      : [];
    const widgetHistoryCount = snapshot.widget_history_count ?? historyIds.length;
    return {
      ...snapshot,
      widget_history_count: widgetHistoryCount
    };
  }, []);

  // Drawer controlled by parent via `onOpenTimeline` prop

  // Navigate function to be passed to calendar and widgets
  const navigateToEvent = useCallback((eventid) => {
    if (typeof onOpenTimeline === 'function') {
      onOpenTimeline(eventid);
    }
  }, [onOpenTimeline]);

  const calendarWidget = useMemo(() => {
    if (!showCalendarWidget) {
      return null;
    }
    return <CalendarOptimized caseId={caseid} navigateTo={navigateToEvent} variant="widget" />;
  }, [caseid, navigateToEvent, showCalendarWidget]);

  const trendWidget = useMemo(() => {
    if (!showTrendWidget) {
      return null;
    }
    return <TimeLineTrendChart variant="widget" />;
  }, [showTrendWidget]);

  useEffect(() => {
    setActiveSnapshot(null);
    setSnapshotOverrides({});
    setSelectedSnapshotId('');
    setSnapshotError('');
    refreshSnapshots();
  }, [caseid, refreshSnapshots]);

  useEffect(() => {
    if (!subscribeToMessages) {
      return undefined;
    }

    const unsubscribe = subscribeToMessages((message) => {
      if (!message || !message.command) {
        return;
      }

      if (message.command === 'getDashboardSnapshots') {
        setSnapshotsLoading(false);
        if (message.data?.status === 'success') {
          if (!caseid || String(message.data.caseid || '') === String(caseid)) {
            setSnapshots(message.data.snapshots || []);
            setSnapshotError('');
          }
        } else if (message.data?.status === 'error') {
          setSnapshotError(message.data.message || 'Failed to load dashboard snapshots');
        }
      }

      if (message.command === 'saveDashboardSnapshot') {
        setIsSavingSnapshot(false);
        if (message.data?.status === 'success') {
          const snapshot = normalizeSnapshotListItem(message.data.snapshot);
          if (snapshot && (!caseid || String(snapshot.case_id || '') === String(caseid))) {
            setSnapshots((prev) => {
              const filtered = prev.filter((item) => String(item.id) !== String(snapshot.id));
              return [snapshot, ...filtered];
            });
          } else {
            refreshSnapshots();
          }
          setSaveDialogOpen(false);
          setSaveComment('');
          setSaveError('');
        } else if (message.data?.status === 'error') {
          setSaveError(message.data.message || 'Failed to save dashboard snapshot');
        }
      }

      if (message.command === 'loadDashboardSnapshot') {
        if (message.data?.status === 'success') {
          const snapshot = message.data.snapshot;
          if (snapshot && (!caseid || String(snapshot.case_id || '') === String(caseid))) {
            const overrides = buildSnapshotOverrides(message.data.logs || []);
            setSnapshotOverrides(overrides);
            setActiveSnapshot(snapshot);
            setSnapshotError('');
          }
        } else if (message.data?.status === 'error') {
          setSnapshotError(message.data.message || 'Failed to load dashboard snapshot');
        }
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, caseid, buildSnapshotOverrides, normalizeSnapshotListItem, refreshSnapshots]);

  const handleSaveSnapshot = () => {
    if (!sendMessage || !caseid) {
      return;
    }
    setIsSavingSnapshot(true);
    setSaveError('');
    sendMessage({
      command: 'saveDashboardSnapshot',
      caseid,
      comment: saveComment
    });
  };

  const handleLoadSnapshot = useCallback((snapshotId) => {
    if (!sendMessage || !caseid || !snapshotId) {
      return;
    }
    setSnapshotError('');
    sendMessage({
      command: 'loadDashboardSnapshot',
      caseid,
      snapshot_id: snapshotId
    });
  }, [sendMessage, caseid]);

  const handleClearSnapshot = useCallback(() => {
    setActiveSnapshot(null);
    setSnapshotOverrides({});
    setSnapshotError('');
  }, []);

  useEffect(() => {
    if (!selectedSnapshotId) {
      handleClearSnapshot();
      return;
    }
    handleLoadSnapshot(selectedSnapshotId);
  }, [selectedSnapshotId, handleClearSnapshot, handleLoadSnapshot]);

  return (
    <Box
      data-project-view={isDagMode ? 'flows' : 'dashboard'}
      sx={{
        animation: 'dashboardFade 0.45s ease both',
        '@keyframes dashboardFade': {
          '0%': { opacity: 0, transform: 'translateY(12px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      {/* Main Content - Widgets Only */}
      <Box sx={{ padding: isDagMode ? 0 : isMobile ? '4px' : '5px', mt: isDagMode ? 0 : isMobile ? 0.5 : 1 }}>
        <WidgetDashboardHeader
          templateInfo={templateInfo}
          snapshots={snapshots}
          isLoading={snapshotsLoading}
          selectedSnapshotId={selectedSnapshotId}
          onSnapshotSelect={setSelectedSnapshotId}
          projectView={projectView}
          onProjectViewSelect={handleProjectViewSelect}
          projectViewContainer={projectViewContainer}
          isSaving={isSavingSnapshot}
          isSnapshotMode={isSnapshotMode}
          onSaveSnapshot={() => setSaveDialogOpen(true)}
          onClearSnapshot={handleClearSnapshot}
          error={snapshotError}
          activeSnapshot={activeSnapshot}
          showHiddenWidgets={effectiveShowHiddenWidgets}
          onToggleHiddenWidgets={canManageProject ? toggleShowHiddenWidgets : null}
          showAddWidget={canManageProject && !isSnapshotMode}
          onAddWidget={() => setShowAddWidgetModal(true)}
        />
        <ProjectChat key={`${user.organizationId}:${user.username}:${user.permissions}:${user.validation_mode}:${canManageProject}:${caseid}`} caseid={caseid} disabled={isSnapshotMode && !isDagMode}
          identityKey={`${user.organizationId}:${user.username}:${caseid}`}
          onAvailabilityChange={onChatAvailabilityChange}
          onOpenEvent={navigateToEvent}
          onSetup={() => {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('flowInteraction', 'connector');
            setSearchParams(nextParams);
            handleProjectViewSelect(DAG_VIEW_ID);
          }} />

        {templateSetupStatusCheckFailed && !isDagMode && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('template.setup_status_error', 'Could not check whether setup is complete. Open Flows to review the guide.')}
          </Alert>
        )}

        <Box ref={flowViewportRef} sx={{ mb: isDagMode ? 0 : 2 }}>
          {isDagMode ? (
            <Suspense fallback={<WidgetLoadingSkeleton layout="grid" showHeader={false} showAddButton={false} />}>
              <ProjectDagView
                caseid={caseid}
                canManageProject={canManageProject}
                onOpenDashboard={handleOpenDashboard}
                onOpenTimeline={onOpenTimeline}
                initialInteraction={initialFlowInteraction}
                onInitialInteractionHandled={handleInitialFlowInteractionHandled}
              />
            </Suspense>
          ) : (
          <Suspense
            fallback={
              <WidgetLoadingSkeleton
                layout="grid"
                showHeader={false}
                showAddButton={canManageProject && !isSnapshotMode}
              />
            }
          >
            <Widgets
              caseid={caseid}
              layout="grid"
              showHeader={false}
              showAddButton={false}
              onAddWidget={() => setShowAddWidgetModal(true)}
              autoOpenWidgetTag={autoOpenWidgetTag}
              onAutoOpenWidgetTagHandled={onAutoOpenWidgetTagHandled}
              calendarComponent={calendarWidget}
              trendChartComponent={trendWidget}
              snapshotOverrides={snapshotOverrides}
              isSnapshotMode={isSnapshotMode}
              showHiddenWidgets={effectiveShowHiddenWidgets}
            />
          </Suspense>
          )}
        </Box>

        {/* Add Widget Modal */}
        {!isDagMode && showAddWidgetModal && <Suspense
          fallback={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('widget.modal_loading', 'Loading...')}
              </Typography>
            </Box>
          }
        >
          <AddWidgetModal
            open={showAddWidgetModal}
            onClose={() => setShowAddWidgetModal(false)}
            caseid={caseid}
          />
        </Suspense>}
      </Box>

      <Dialog
        open={saveDialogOpen}
        onClose={() => {
          if (!isSavingSnapshot) {
            setSaveDialogOpen(false);
            setSaveError('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('dashboard.snapshot_save_title', 'Save dashboard snapshot')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('dashboard.snapshot_save_by', 'Saved by')} {user.username || t('dashboard.snapshot_unknown_user', 'Unknown')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('dashboard.snapshot_comment', 'Comment (optional)')}
            value={saveComment}
            onChange={(event) => setSaveComment(event.target.value)}
            disabled={isSavingSnapshot}
          />
          {saveError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSaveDialogOpen(false);
              setSaveError('');
            }}
            disabled={isSavingSnapshot}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSnapshot}
            disabled={isSavingSnapshot}
          >
            {isSavingSnapshot
              ? t('dashboard.snapshot_saving', 'Saving...')
              : t('dashboard.snapshot_save_confirm', 'Save snapshot')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer and FAB moved to parent Dashboard component. */}
    </Box>
  );
};

export default WidgetDashboard;
